// Funções puras da página Industrialização (Máquinas). Sem JSX, sem efeitos.
// Todas as visões (kanban, tabela, calendário, histórico) partem daqui.

import { formatarData, parseNumero } from "../../utils/calculos";
import { hasValue } from "../../utils/data";
import { F } from "../../utils/gerencialFields";
import {
  SEQUENCIA_MAQUINAS,
  formatLoteLabel,
  getColunasMaquinas,
  getIndustrializacaoDeadline,
  getIndustrializacaoRowState,
  isIndustrializacaoPending,
} from "../../utils/industrializacao";
import { buildDeadlineStatus, compareDatesAsc, isBeforeToday, toDate } from "../../utils/status";

function getSortedDates(values) {
  return values.filter((value) => toDate(value)).sort((left, right) => compareDatesAsc(left, right));
}

export function getEarliestDate(values) {
  const dates = getSortedDates(values);
  return dates[0] ?? null;
}

export function getLatestDate(values) {
  const dates = getSortedDates(values);
  return dates.length ? dates[dates.length - 1] : null;
}

export function formatDateRange(values) {
  const inicio = getEarliestDate(values);
  const fim = getLatestDate(values);

  if (!inicio && !fim) {
    return "—";
  }

  if (!inicio || !fim || compareDatesAsc(inicio, fim) === 0) {
    return formatarData(inicio || fim);
  }

  return `${formatarData(inicio)} a ${formatarData(fim)}`;
}

// Nomes das operações na ordem em que o usuário as vê na tela.
const NOMES_OPERACOES = getColunasMaquinas().map((operacao) => operacao.titulo.toLowerCase());
export const LISTA_OPERACOES = `${NOMES_OPERACOES.slice(0, -1).join(", ")} ou ${
  NOMES_OPERACOES[NOMES_OPERACOES.length - 1]
}`;

function getPendenciaLabel(aguardandoPorOperacao) {
  const partes = getColunasMaquinas()
    .filter((operacao) => aguardandoPorOperacao[operacao.chave] > 0)
    .map((operacao) => `${aguardandoPorOperacao[operacao.chave]} ${operacao.titulo.toLowerCase()}`);

  return partes.length ? partes.join(" • ") : "Sem pendências";
}

export function getAmbienteTone(ambiente) {
  if (!ambiente.pendente) {
    return "success";
  }

  if (isBeforeToday(ambiente.dataLimite)) {
    return "danger";
  }

  return "default";
}

export const INITIAL_FILTERS = {
  busca: "",
  status: "TODOS",
  cliente: "TODOS",
  lote: "",
  limiteDe: "",
  limiteAte: "",
};

export function getUniqueOptions(values) {
  return [...new Set(values.filter(Boolean).map((value) => String(value).trim()))].sort((left, right) =>
    left.localeCompare(right, "pt-BR", { numeric: true, sensitivity: "base" })
  );
}

function matchesDateRange(value, from, to) {
  const date = toDate(value);
  const fromDate = toDate(from);
  const toDateValue = toDate(to);

  if (!date) return false;
  if (fromDate && date < fromDate) return false;
  if (toDateValue && date > toDateValue) return false;
  return true;
}

function compararLotes(left, right) {
  const byLimite = compareDatesAsc(left.dataLimiteMaquinas, right.dataLimiteMaquinas);
  if (byLimite !== 0) return byLimite;
  const byContrato = compareDatesAsc(left.entregaContrato, right.entregaContrato);
  if (byContrato !== 0) return byContrato;
  return left.loteLabel.localeCompare(right.loteLabel, "pt-BR", {
    numeric: true,
    sensitivity: "base",
  });
}

function montarLote(lote) {
  const clientes = [...new Set(lote.linhas.map((row) => row[F.NOME_DO_CLIENTE]).filter(hasValue))];
  const clientePrincipal = clientes[0] || "Cliente não informado";
  const clienteResumo =
    clientes.length > 1 ? `${clientePrincipal} +${clientes.length - 1}` : clientePrincipal;

  const dataLimiteMaquinas = getEarliestDate(
    lote.linhas.map((row) => getIndustrializacaoDeadline(row))
  );
  const entregaContrato = getEarliestDate(lote.linhas.map((row) => row[F.ENTREGA_PREVISTA]));
  const dataOtimizacao = formatDateRange(lote.linhas.map((row) => row[F.OTIMIZACAO_DO_LOTE]));
  const dataMdf = formatDateRange(lote.linhas.map((row) => row[F.MDF_BORDO]));
  const datasPorOperacao = Object.fromEntries(
    SEQUENCIA_MAQUINAS.map((operacao) => [
      operacao.chave,
      formatDateRange(lote.linhas.map((row) => row[operacao.campo])),
    ])
  );
  const valorTotal = lote.linhas.reduce(
    (accumulator, row) => accumulator + (parseNumero(row[F.VALOR_DO_AMBIENTE]) || 0),
    0
  );

  const aguardandoPorOperacao = Object.fromEntries(
    SEQUENCIA_MAQUINAS.map((operacao) => [
      operacao.chave,
      lote.pendentes.filter((row) => !hasValue(row[operacao.campo])).length,
    ])
  );

  const statusInfo = dataLimiteMaquinas
    ? buildDeadlineStatus(dataLimiteMaquinas, {
        overdueLabel: "Máquinas em atraso",
        warningLabel: "Máquinas nesta semana",
        fallbackLabel: "Máquinas no prazo",
      })
    : {
        status: "Sem data limite",
        statusVariant: "warning",
      };

  const ambientes = lote.linhas
    .map((row) => ({
      ambiente: row[F.AMBIENTE] || "Ambiente não informado",
      cliente: row[F.NOME_DO_CLIENTE] || null,
      valor: row[F.VALOR_DO_AMBIENTE],
      dataLimite: getIndustrializacaoDeadline(row),
      entregaContrato: row[F.ENTREGA_PREVISTA],
      mdfBordo: row[F.MDF_BORDO],
      datas: Object.fromEntries(
        SEQUENCIA_MAQUINAS.map((operacao) => [operacao.chave, row[operacao.campo]])
      ),
      status: getIndustrializacaoRowState(row),
      pendente: isIndustrializacaoPending(row),
      // Linha crua: o histórico de comentários do ambiente lê a chave dela.
      row,
    }))
    .sort((left, right) => {
      if (left.pendente !== right.pendente) {
        return left.pendente ? -1 : 1;
      }

      const byLimite = compareDatesAsc(left.dataLimite, right.dataLimite);
      return byLimite !== 0
        ? byLimite
        : left.ambiente.localeCompare(right.ambiente, "pt-BR", {
            sensitivity: "base",
          });
    });

  return {
    id: lote.loteId,
    loteLabel: formatLoteLabel(lote.loteId),
    clientePrincipal,
    clienteResumo,
    clientes,
    totalAmbientes: lote.linhas.length,
    totalPendentes: lote.pendentes.length,
    dataLimiteMaquinas,
    entregaContrato,
    dataOtimizacao,
    dataMdf,
    datasPorOperacao,
    valorTotal,
    aguardandoPorOperacao,
    pendenciasLabel: getPendenciaLabel(aguardandoPorOperacao),
    ambientes,
    ...statusInfo,
  };
}

/** Lotes com ao menos um ambiente pendente nas máquinas, do limite mais cedo ao mais tarde. */
export function buildLotes(dados) {
  const agrupados = new Map();

  dados.forEach((row) => {
    if (!hasValue(row[F.NUMERO_DO_LOTE])) {
      return;
    }

    const loteId = String(row[F.NUMERO_DO_LOTE]).trim();
    if (!agrupados.has(loteId)) {
      agrupados.set(loteId, { loteId, linhas: [], pendentes: [] });
    }

    const lote = agrupados.get(loteId);
    lote.linhas.push(row);

    if (isIndustrializacaoPending(row)) {
      lote.pendentes.push(row);
    }
  });

  return [...agrupados.values()]
    .filter((lote) => lote.pendentes.length > 0)
    .map(montarLote)
    .sort(compararLotes);
}

/**
 * Um lote qualquer pelo número, mesmo já concluído nas máquinas — o calendário
 * e o histórico abrem lotes que saíram da fila pendente.
 */
export function buildLotePorId(dados, loteId) {
  const alvo = String(loteId ?? "").trim();
  const linhas = dados.filter(
    (row) => hasValue(row[F.NUMERO_DO_LOTE]) && String(row[F.NUMERO_DO_LOTE]).trim() === alvo
  );
  if (!linhas.length) return null;
  return montarLote({
    loteId: alvo,
    linhas,
    pendentes: linhas.filter(isIndustrializacaoPending),
  });
}

export function filtrarLotes(lotes, filters) {
  const query = filters.busca.trim().toLowerCase();

  return lotes.filter((lote) => {
    if (
      query &&
      ![lote.loteLabel, lote.clienteResumo, lote.pendenciasLabel, lote.status]
        .join(" ")
        .toLowerCase()
        .includes(query)
    ) {
      return false;
    }

    if (filters.status !== "TODOS" && lote.status !== filters.status) {
      return false;
    }

    if (filters.cliente !== "TODOS" && lote.clientePrincipal !== filters.cliente) {
      return false;
    }

    if (
      filters.lote.trim() &&
      !lote.loteLabel.toLowerCase().includes(filters.lote.trim().toLowerCase())
    ) {
      return false;
    }

    if (
      (filters.limiteDe || filters.limiteAte) &&
      !matchesDateRange(lote.dataLimiteMaquinas, filters.limiteDe, filters.limiteAte)
    ) {
      return false;
    }

    return true;
  });
}

// O lote cai na PRIMEIRA operação da SEQUENCIA_MAQUINAS (corte -> furação ->
// bordo) que ainda tem algum ambiente sem data — o lote acompanha o ambiente
// mais atrasado.
export function agruparLotesPorOperacao(lotes) {
  const agrupados = Object.fromEntries(SEQUENCIA_MAQUINAS.map((operacao) => [operacao.chave, []]));

  (lotes || []).forEach((lote) => {
    const pendente = SEQUENCIA_MAQUINAS.find((operacao) =>
      lote.ambientes.some((ambiente) => !hasValue(ambiente.datas[operacao.chave]))
    );

    if (pendente) {
      agrupados[pendente.chave].push(lote);
    }
  });

  Object.values(agrupados).forEach((lotesDaOperacao) => lotesDaOperacao.sort(compararLotes));
  return agrupados;
}

export function buildFiltrosAtivos(filters) {
  const chips = [];
  if (filters.busca.trim()) chips.push(`Busca: ${filters.busca.trim()}`);
  if (filters.status !== "TODOS") chips.push(`Status: ${filters.status}`);
  if (filters.cliente !== "TODOS") chips.push(`Cliente: ${filters.cliente}`);
  if (filters.lote.trim()) chips.push(`Lote: ${filters.lote.trim()}`);
  if (filters.limiteDe || filters.limiteAte) {
    chips.push(`Limite: ${filters.limiteDe || "inicio"} a ${filters.limiteAte || "fim"}`);
  }
  return chips;
}

export function buildResumo(lotes) {
  const totalAmbientesPendentes = lotes.reduce(
    (accumulator, lote) => accumulator + lote.totalPendentes,
    0
  );
  const lotesEmAtraso = lotes.filter((lote) => lote.statusVariant === "danger").length;
  const valorTotal = lotes.reduce((accumulator, lote) => accumulator + lote.valorTotal, 0);
  const aguardandoPorOperacao = Object.fromEntries(
    SEQUENCIA_MAQUINAS.map((operacao) => [
      operacao.chave,
      lotes.reduce((accumulator, lote) => accumulator + lote.aguardandoPorOperacao[operacao.chave], 0),
    ])
  );

  return {
    totalLotes: lotes.length,
    totalAmbientesPendentes,
    lotesEmAtraso,
    valorTotal,
    aguardandoPorOperacao,
    totalPendencias: Object.values(aguardandoPorOperacao).reduce(
      (accumulator, quantidade) => accumulator + quantidade,
      0
    ),
  };
}
